import type { TOC } from '@ember/component/template-only';
import { Heading, Text } from 'tio-ui/components/typography';
import { getCtaTokens } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// CtaBanner – soft gradient call-to-action block for page bottoms
// ---------------------------------------------------------------------------

/**
 * Color theme options for the CTA banner gradient
 */
export type CtaBannerTheme =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'rose'
  | 'indigo'
  | 'pink';

interface CtaBannerSignature {
  Args: {
    /** Heading text */
    heading: string;
    /** Description text */
    description?: string;
    /** Color theme for the gradient background */
    theme?: CtaBannerTheme;
  };
  Blocks: {
    /** Action content (button, link) */
    action?: [];
  };
  Element: HTMLDivElement;
}

const getThemeClasses = (theme?: CtaBannerTheme) => getCtaTokens(theme);

/**
 * CtaBanner - A soft gradient call-to-action block for page bottoms
 *
 * Displays a centered heading, description, and action slot on a light
 * gradient background. Used at the bottom of feature pages to encourage
 * the user to take a next step.
 *
 * @example Basic usage
 * ```gts
 * import CtaBanner from 'tio-reimagined/components/cta-banner';
 *
 * <CtaBanner
 *   @heading="Balance Student Loans & Retirement"
 *   @description="Our optimizer helps you allocate benefits for maximum impact."
 *   @theme="emerald"
 * >
 *   <:action>
 *     <LinkTo @route="authenticated.reimagined.finance.optimizer">
 *       <Button @intent="primary">Optimize My Benefits</Button>
 *     </LinkTo>
 *   </:action>
 * </CtaBanner>
 * ```
 */
const CtaBanner: TOC<CtaBannerSignature> = <template>
  {{#let (getThemeClasses @theme) as |styles|}}
    <div data-test-cta-banner class="{{styles.gradient}} rounded-2xl p-8 text-center" ...attributes>
      <Heading
        data-test-cta-banner-heading
        @level={{3}}
        class="text-xl font-bold mb-2 {{styles.heading}}"
      >
        {{@heading}}
      </Heading>
      {{#if @description}}
        <Text data-test-cta-banner-description class="mb-6 {{styles.description}}">
          {{@description}}
        </Text>
      {{/if}}
      {{#if (has-block "action")}}
        <div data-test-cta-banner-action>
          {{yield to="action"}}
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default CtaBanner;

export type { CtaBannerSignature };
